import type { ReactNode, ComponentProps } from "react";
import { AppLayout, PageContent } from "@/components/layout/AppLayout";
import { SectionBar } from "@/components/layout/SectionBar";
import { NavSidebar } from "@/components/layout/NavSidebar";
import { DetailPanel } from "@/components/layout/DetailPanel";
import { SidePanelProvider, useSidePanel } from "@/contexts/SidePanelContext";
import { useSidebarState } from "@/hooks/useSidebarState";
import type { BreadcrumbItem } from "@/components/layout/SectionBar";
import type { SidebarItem } from "@/types/navigation";

function SectionLayoutInner({
  sidebarKey,
  sectionTitle,
  sectionIcon,
  sidebarItems,
  pageTitle,
  breadcrumbs = [],
  rightSlot,
  children,
}: {
  sidebarKey: string;
  sectionTitle: string;
  sectionIcon: React.ElementType;
  sidebarItems: SidebarItem[];
  pageTitle: string;
  breadcrumbs?: BreadcrumbItem[];
  rightSlot?: ReactNode;
  children: ReactNode;
}) {
  const [sidebarOpen, setSidebarOpen, onClose] = useSidebarState(sidebarKey);
  const {
    isOpen: panelOpen,
    title: panelTitle,
    content: panelContent,
    close: panelClose,
  } = useSidePanel();

  return (
    <AppLayout title={sectionTitle} icon={sectionIcon}>
      <SectionBar
        pageTitle={pageTitle}
        breadcrumbs={breadcrumbs}
        sidebarOpen={sidebarOpen}
        onMenuClick={() => setSidebarOpen((v) => !v)}
        rightSlot={rightSlot}
      />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <NavSidebar
          open={sidebarOpen}
          onClose={onClose}
          title={sectionTitle}
          items={sidebarItems}
        />
        <PageContent>{children}</PageContent>
        <DetailPanel open={panelOpen} onClose={panelClose} title={panelTitle}>
          {panelContent}
        </DetailPanel>
      </div>
    </AppLayout>
  );
}

export function SectionLayout(props: ComponentProps<typeof SectionLayoutInner>) {
  return (
    <SidePanelProvider>
      <SectionLayoutInner {...props} />
    </SidePanelProvider>
  );
}
